import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@xanectech/ui/components';
import { useCreateMessage } from '@xanectech/data';
// import { useAuth } from '@xanectech/auth';
import { useState } from 'react';
import { UserSearchInput } from './UserSearchInput';
import { CompanySearchInput } from './CompanySearchInput';

import { createLogger } from '@xanectech/lib';

const logger = createLogger('Components - CreateMessageDialog');

interface CreateMessageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  messageType: 'admin' | 'company';
}

export const CreateMessageDialog = ({
  isOpen,
  onClose,
  messageType,
}: CreateMessageDialogProps) => {
  // const { user } = useAuth();
  const createMessage = useCreateMessage();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState<'info' | 'warning' | 'success' | 'error'>(
    'info',
  );
  const [targetType, setTargetType] = useState<'system' | 'user' | 'company'>(
    'system',
  );
  const [companyId, setCompanyId] = useState('');
  const [userId, setUserId] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    createMessage
      .mutateAsync({
        title,
        content,
        type,
        targetType,
        companyId: targetType === 'company' ? companyId : null,
        userId: targetType === 'user' ? userId : null,
      })
      .then(() => {
        setTitle('');
        setContent('');
        setType('info');
        setTargetType('system');
        setCompanyId('');
        setUserId('');
        onClose();
      })
      .catch((error: unknown) => {
        logger.error('Failed to create message:', {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
      });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle>Create New Message</DialogTitle>
          <DialogDescription>
            {messageType === 'admin'
              ? 'Create a system, company, or user-specific message'
              : 'Create a company message for your organization'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='title'>Title</Label>
            <Input
              id='title'
              value={title}
              onChange={(e) => setTitle(String(e.target.value))}
              placeholder='Message title'
              required
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='content'>Content</Label>
            <Textarea
              id='content'
              value={content}
              onChange={(e) => setContent(String(e.target.value))}
              placeholder='Message content'
              rows={4}
              required
            />
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='type'>Message Type</Label>
              <Select
                value={type}
                onValueChange={(
                  value: 'info' | 'warning' | 'success' | 'error',
                ) => setType(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='info'>Info</SelectItem>
                  <SelectItem value='warning'>Warning</SelectItem>
                  <SelectItem value='success'>Success</SelectItem>
                  <SelectItem value='error'>Error</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {messageType === 'admin' && (
              <div className='space-y-2'>
                <Label htmlFor='targetType'>Target Type</Label>
                <Select
                  value={targetType}
                  onValueChange={(value: 'system' | 'user' | 'company') =>
                    setTargetType(value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='system'>System-wide</SelectItem>
                    <SelectItem value='company'>Company-specific</SelectItem>
                    <SelectItem value='user'>User-specific</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {(messageType === 'company' || targetType === 'company') && (
            <div className='space-y-2'>
              <Label htmlFor='companyId'>Company</Label>
              <CompanySearchInput
                value={companyId || null}
                onChange={(selectedId) => setCompanyId(selectedId || '')}
                required={targetType === 'company'}
                disabled={createMessage.isPending}
              />
            </div>
          )}

          {targetType === 'user' && (
            <div className='space-y-2'>
              <Label htmlFor='userId'>User ID</Label>
              <UserSearchInput
                value={userId || null}
                onChange={(selectedId) => setUserId(selectedId || '')}
                required={targetType === 'user'}
                disabled={createMessage.isPending}
              />
            </div>
          )}

          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={createMessage.isPending}>
              {createMessage.isPending ? 'Creating...' : 'Create Message'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
